import {
  useFloating as useFloatingUi,
  shift,
  size,
  flip,
  offset,
  hide,
  inline,
  autoUpdate,
  limitShift,
} from '@floating-ui/react';
import type {
  Placement as FloatingPlacement,
  Strategy as FloatingStrategy,
  Middleware,
  UseFloatingReturn,
} from '@floating-ui/react';

const POPOVER_PADDING = 4;

export type UseFloatingStrategyOptions = {
  nodeId?: string;
  placement: FloatingPlacement;
  floatingStrategy?: FloatingStrategy;
  offsetValue?: number;
  inlineEnabled?: boolean;
  flipEnabled?: boolean;
  fallbackPlacements?: FloatingPlacement[];
  hideOptions?: {referenceHidden?: boolean};
  shiftEnabled?: boolean;
  shiftOptions?: {padding?: number; altBoundary?: boolean};
  sizeOptions?: {
    maxWidth?: 'fill' | boolean;
    maxHeight?: 'fill' | boolean;
    matchWidth?: boolean;
    padding?: number;
    altBoundary?: boolean;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  additionalMiddleware?: Middleware[];
};

/**
 * Shared hook for floating-ui positioning logic used by Popover and other overlay components.
 * Encapsulates the standard middleware chain with extension points for custom middleware.
 */
export function useFloating<T extends HTMLDivElement>({
  nodeId,
  placement,
  floatingStrategy,
  offsetValue = POPOVER_PADDING,
  inlineEnabled,
  flipEnabled = true,
  fallbackPlacements,
  hideOptions,
  shiftEnabled = true,
  shiftOptions = {},
  sizeOptions,
  open,
  onOpenChange,
  additionalMiddleware = [],
}: UseFloatingStrategyOptions): UseFloatingReturn<T> {
  const floating = useFloatingUi<T>({
    nodeId,
    placement,
    strategy: floatingStrategy,
    whileElementsMounted: autoUpdate,
    middleware: [
      inlineEnabled && inline(),
      offset(offsetValue),
      hideOptions?.referenceHidden && hide(),
      hide({strategy: 'escaped'}),
      flip(({middlewareData}) => ({
        fallbackPlacements,
        mainAxis: flipEnabled,
        crossAxis:
          fallbackPlacements && fallbackPlacements.length > 0
            ? placement?.includes('-')
            : flipEnabled,
        altBoundary: middlewareData?.hide?.escaped,
        /**
         * The `bestFit` strategy doesn't play nicely when comparing
         * overflows that are very close to each other (e.g. less than 1px difference).
         * When paired with the `altBoundary` option, this can cause the popover to
         * flip into a position that is clipped by the viewport.
         */
        fallbackStrategy: middlewareData?.hide?.escaped
          ? 'initialPlacement'
          : 'bestFit',
      })),
      sizeOptions &&
        size({
          padding: sizeOptions.padding || POPOVER_PADDING,
          altBoundary: sizeOptions.altBoundary,
          apply({availableHeight, availableWidth, elements, rects}) {
            // intentionally using style here to avoid generating a new class
            // for each pixel value of width|max{width|height}.
            const style = elements.floating?.style || {};
            if (sizeOptions.maxWidth) {
              Object.assign(style, {maxWidth: `${availableWidth}px`});
            }
            if (sizeOptions.maxHeight) {
              Object.assign(style, {maxHeight: `${availableHeight}px`});
            }
            if (sizeOptions.matchWidth) {
              Object.assign(style, {width: `${rects.reference.width}px`});
            }
          },
        }),
      shift({
        mainAxis: shiftEnabled,
        padding: shiftOptions.padding || POPOVER_PADDING,
        altBoundary: shiftOptions.altBoundary,
        limiter: limitShift(),
      }),

      ...additionalMiddleware,
    ].filter(Boolean),
    open,
    onOpenChange,
  });

  return floating;
}

